import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { loadStripe } from "@stripe/stripe-js";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useLocation } from "react-router-dom";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const UpdatePricingPlans = ({ isSidebarOpen }) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [currentPlan, setCurrentPlan] = useState({
    name: "Standard",
    billingType: "Monthly",
  }); // Initial Plan
  const navigate = useNavigate();
  const location = useLocation();
  const buyDate = new Date().toLocaleDateString("en-GB");
  const endDate = isAnnual
    ? new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toLocaleDateString("en-GB")
    : new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleDateString("en-GB");
  const plans = {
    monthly: [
      {
        name: "Basic",
        price: "0",
        description: "Free Forever",
        features: [
          "Up to 1 connected account",
          "AI-powered Cloud Inventory & Mapping",
          "Basic Cost Optimization",
          "Security & Compliance Dashboard",
        ],
        image: "piggy-bank.png",
        buttonType: "outline-primary",
      },
      {
        name: "Standard",
        price: "49",
        description: "For small to medium businesses",
        features: [
          "Up to 2 connected accounts",
          "All Basic features plus:",
          "AI-driven Vulnerability Scanner",
          "Alerts & Notifications",
          "Enhanced Cost Optimization",
          "Advanced Security & Compliance Reports",
        ],
        image: "coins.png",
        buttonType: "secondary",
      },
      {
        name: "Professional",
        price: "99",
        description: "For growing organizations",
        features: [
          "Up to 3 connected accounts",
          "All Standard features plus:",
          "Multi-Cloud AI Insights",
          "Advanced Reporting & Analytics",
          "API Access & Integrations",
          "Priority Support",
          "Automated Compliance Checks",
          "Command Center: Prebuilt tools",
        ],
        image: "money-bag.png",
        buttonType: "outline-primary",
      },
    ],
    annually: [
      {
        name: "Basic",
        price: "0",
        description: "Free Forever",
        features: [
          "Up to 1 connected account",
          "AI-powered Cloud Inventory & Mapping",
          "Basic Cost Optimization",
          "Security & Compliance Dashboard",
        ],
        image: "piggy-bank.png",
        buttonType: "outline-primary",
      },
      {
        name: "Standard",
        price: "490",
        description: "For small to medium businesses",
        features: [
          "Up to 2 connected accounts",
          "All Basic features plus:",
          "AI-driven Vulnerability Scanner",
          "Alerts & Notifications",
          "Enhanced Cost Optimization",
          "Advanced Security & Compliance Reports",
        ],
        image: "coins.png",
        buttonType: "secondary",
      },
      {
        name: "Professional",
        price: "990",
        description: "For growing organizations",
        features: [
          "Up to 3 connected accounts",
          "All Standard features plus:",
          "Multi-Cloud AI Insights",
          "Advanced Reporting & Analytics",
          "API Access & Integrations",
          "Priority Support",
          "Automated Compliance Checks",
          "Command Center: Prebuilt tools",
        ],
        image: "money-bag.png",
        buttonType: "outline-primary",
      },
    ],
  };

  const currentPlans = isAnnual ? plans.annually : plans.monthly;

// Add Enterprise plan dynamically
const enterprisePlan = {
  name: "Enterprise",
  price: "Custom",
  description: "For large organizations",
  features: [
    "4 or more connected accounts",
    "All Professional features plus:",
    "AI-Driven Automation & Recommendations",
    "Dedicated Account Management",
    "SLA & 24/7 Support",
    "Custom Feature Requests & Scalability",
    "Command Center with Extended Enterprise Controls"
  ],
  image: "money-bag.png",
  buttonType: "warning"
};
  const organizationID = localStorage.getItem("organizationID");
  const handlePlanSelect = async (plan) => {
    if (plan.name === currentPlan.name && currentPlan.billingType === (isAnnual ? "Annually" : "Monthly")) {
      Swal.fire("Info", "You are already subscribed to this plan.", "info").then(() => {
        navigate("/dashboard/billing-detail");
      });
      return;
    }

    Swal.fire({
      title: "Confirm Plan Update",
      text: `Are you sure you want to switch to the ${plan.name} plan?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update plan",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const updatedPlan = {
          name: plan.name,
          billingType: isAnnual ? "Annually" : "Monthly",
          price: plan.price,
        };

        const organizationID = localStorage.getItem("organizationID");
        const apiDomain = process.env.REACT_APP_API_DOMAIN;

        if (plan.price === "0") {
          // For free plans, store in DB and localStorage immediately
          try {
            const dbResponse = await fetch(`${apiDomain}/updateUser`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                organizationID,
                subscriptionPlan: JSON.stringify(updatedPlan),
                buySubscriptionPlanDate: buyDate,
                endSubscriptionDate: endDate
              }),
            });

            if (!dbResponse.ok) {
              const errorData = await dbResponse.json();
              Swal.fire("Error", errorData.message || "Failed to update plan in the database.", "error");
              return;
            }

            // Save to localStorage after DB save
            setCurrentPlan(updatedPlan);
            localStorage.setItem("selectedPlan", JSON.stringify(updatedPlan));

            Swal.fire(
              "Updated!",
              `Your plan has been updated to ${plan.name} (${updatedPlan.billingType}).`,
              "success"
            );
            window.location.href = "/dashboard/billing-detail";
          } catch (error) {
            console.error("Error updating the plan in the database:", error);
            Swal.fire("Error", "There was an issue saving your plan. Please try again.", "error");
            return;
          }
        } else {
          // For paid plans, initiate Stripe payment first
          try {
            const stripe = await stripePromise;

            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout-session`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  planName: plan.name,
                  price: parseInt(plan.price) * 100, // Convert to cents
                  billingType: isAnnual ? "Annually" : "Monthly",
                  success_url: `${process.env.REACT_APP_FRONTEND_URL}/dashboard/billing-detail?plan=${plan.name}&price=${plan.price}`,
                  cancel_url: `${process.env.REACT_APP_FRONTEND_URL}/dashboard/billing-detail?error=true`,
                }),
              }
            );

            if (response.ok) {
              const session = await response.json();
              const { error } = await stripe.redirectToCheckout({ sessionId: session.id });

              if (error) {
                throw new Error("Stripe redirection failed.");
              }
            } else {
              const errorData = await response.json();
              Swal.fire("Error", errorData.message || "Unable to process payment.", "error");
            }
          } catch (error) {
            console.error("Error during Stripe integration:", error);
            Swal.fire("Error", "There was an issue processing your payment. Please try again.", "error");
          }
        }
      }
    });
  };



  useEffect(() => {
    const updatePlanAfterSuccess = async (planName, planPrice) => {
      const organizationID = localStorage.getItem("organizationID");
      const apiDomain = process.env.REACT_APP_API_DOMAIN;

      const updatedPlan = {
        name: planName,
        billingType: isAnnual ? "Annually" : "Monthly",
        price: planPrice,
      };

      try {
        // Update the database
        const dbResponse = await fetch(`${apiDomain}/updateUser`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationID,
            subscriptionPlan: JSON.stringify(updatedPlan),
            buySubscriptionPlanDate: buyDate,
            endSubscriptionDate: endDate
          }),
        });

        if (!dbResponse.ok) {
          const errorData = await dbResponse.json();
          Swal.fire("Error", errorData.message || "Failed to update plan in the database.", "error");
          return;
        }

        // Update localStorage
        localStorage.setItem("selectedPlan", JSON.stringify(updatedPlan));

        // Notify the user
        Swal.fire({
          title: "Account Updated",
          text: `Your plan has been updated to ${planName} successfully!`,
          icon: "success",
          timer: 3000, // 3 seconds
          timerProgressBar: true,
          showConfirmButton: false,
        }).then(() => {
          // Reload the page after redirecting
          window.location.href = "/dashboard/billing-detail";
        });
      } catch (error) {
        console.error("Error updating plan after payment:", error);
        Swal.fire("Error", "Failed to update your plan. Please contact support.", "error");
      }
    };

    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get("plan")) {
      const planName = searchParams.get("plan");
      const planPrice = searchParams.get("price");
      updatePlanAfterSuccess(planName, planPrice);
    }

    if (searchParams.get("error") === "true") {
      Swal.fire(
        "Payment Failed",
        "There was an issue processing your payment. Please try again.",
        "error"
      );
      // Reload the current page after removing query parameters
      window.location.href = location.pathname;
    }
  }, [location, isAnnual]);



  useEffect(() => {
    const storedPlan = JSON.parse(localStorage.getItem("selectedPlan"));
    if (storedPlan) {
      setCurrentPlan(storedPlan);
    }
  }, []);

  return (
    <div
      className="cloudmap-page container-fluid pb-5 bg-light"
      style={{
        marginLeft: isSidebarOpen ? "0px" : "0px",
        transition: "margin-left 0.3s ease",
      }}
    >
      <div
        className="updated-pricing-plans"
        style={{
          width: "100%",
          maxWidth: "1400px",
          transition: "margin-left 0.3s ease",
          margin: "0 auto",
          paddingTop: "2rem",
        }}
      >
        <div className="container pricing-plan py-2">
          <h2 className="text-center mb-3" style={{ color: "#342ead", fontWeight: "bold" }}>ODIR Pricing Plans</h2>
          <p className="text-center text-muted mb-2">
            Choose the plan that's right for your organization
          </p>
          <p className="text-center mb-4">
            You are currently subscribed to the{" "}
            <strong>
              {currentPlan.name} ({currentPlan.billingType})
            </strong>{" "}
            plan.
          </p>

          {/* Billing Toggle */}
          <div className="switch d-flex justify-content-center flex-wrap align-items-center mb-4">
            <span className="me-3 annually-text mb-sm-0 fw-bold">Monthly</span>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="billingToggle"
                onChange={() => setIsAnnual(!isAnnual)}
                checked={isAnnual}
                style={{ width: "2.5rem", height: "1.2rem" }}
              />
              <label
                className="form-check-label monthly-text ms-2 fw-bold"
                htmlFor="billingToggle"
              >
                Annually
              </label>
            </div>
            <span className="ms-2 text-success">Save 16%</span>
          </div>


          {/* Pricing Plans */}
          <div className="row">
            {currentPlans.map((plan, index) => (
              <div className="col-md-4 mb-4" key={index}>
                <div
                  className="card text-center"
                  style={{
                    border:
                      plan.name === currentPlan.name &&
                        currentPlan.billingType === (isAnnual ? "Annually" : "Monthly")
                        ? "1px solid #666cff"
                        : "1px solid #eaeaea",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    overflow: "hidden",
                    height: "100%",
                    minHeight: "600px",
                  }}
                >
                  <div 
                    className="card-header py-3"
                    style={{
                      backgroundColor: "#f8f9fa",
                      color: "#333",
                      borderBottom: "1px solid #eaeaea"
                    }}
                  >
                    <h5 className="mb-0">{plan.name}</h5>
                  </div>
                  <div className="card-body">
                    {plan.name === currentPlan.name &&
                      currentPlan.billingType === (isAnnual ? "Annually" : "Monthly") && (
                        <span
                          className="position-absolute top-0 end-0 m-2 text-primary"
                          style={{fontSize: "0.8rem", fontWeight: "bold"}}
                        >
                          Current Plan
                        </span>
                      )}

                    <img src={`/images/${plan.image}`} alt={`${plan.name} Plan`} className="img-fluid image mb-3" style={{height: "60px", width: "auto", margin: "10px auto"}} />
                    <p className="card-text text-muted mb-3">{plan.description}</p>
                    {plan.name !== "Enterprise" ? (
                      <h3 className="mb-4">
                        <sup>$</sup>
                        {plan.price}
                        <sub className="text-muted">{isAnnual ? "/ year" : "/ month"}</sub>
                      </h3>
                    ) : (
                      <h3 className="mb-4">Contact Us</h3>
                    )}
                    <ul className="list-unstyled mb-4 text-start" style={{minHeight: "220px"}}>
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="mb-2">
                          <i className="fas fa-check-circle text-success me-2"></i> {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      className="btn btn-primary w-100 fw-bold"
                      onClick={() => handlePlanSelect(plan)}
                    >
                      {plan.name === currentPlan.name && currentPlan.billingType === (isAnnual ? "Annually" : "Monthly") ? "Current Plan" : "Switch to " + plan.name}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Enterprise Plan */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card text-center shadow-sm" style={{borderRadius: "8px", overflow: "hidden", border: "1px solid #eaeaea"}}>
                <div className="card-header py-3" style={{backgroundColor: "#f8f9fa", color: "#333", borderBottom: "1px solid #eaeaea"}}>
                  <h5 className="mb-0">Enterprise</h5>
                  <span className="text-muted mt-1" style={{fontSize: "0.9rem", display: "block"}}>Custom Pricing</span>
                </div>
                <div className="card-body d-flex flex-column flex-md-row align-items-center justify-content-between p-4">
                  <div className="text-start mb-3 mb-md-0">
                    <h4>For large organizations</h4>
                    <p className="mb-0">4 or more connected accounts</p>
                    <ul className="list-unstyled mt-3">
                      <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i> All Professional features plus:</li>
                      <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i> AI-Driven Automation & Recommendations</li>
                      <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i> Dedicated Account Management</li>
                      <li className="mb-2"><i className="fas fa-check-circle text-success me-2"></i> SLA & 24/7 Support</li>
                    </ul>
                  </div>
                  <div>
                    <button 
                      className="btn btn-primary btn-lg fw-bold"
                      onClick={() => window.location.href = "mailto:sales@etherops.ai"}
                    >
                      Contact Sales
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePricingPlans;
